import { useEffect } from 'react';

/**
 * SEO component for managing dynamic page metadata
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - Page keywords (comma-separated)
 * @param {string} props.canonical - Canonical URL
 * @param {string} props.image - Open Graph image URL
 * @param {string} props.type - Open Graph type (default: 'website')
 */
export default function SEO({ 
  title, 
  description, 
  keywords,
  canonical,
  image = 'https://yut-game.s3.us-west-2.amazonaws.com/yut-icon.png',
  type = 'website'
}) {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
      updateMetaTag('property', 'og:title', title);
      updateMetaTag('name', 'twitter:title', title);
      updateMetaTag('name', 'title', title);
    }

    // Update description
    if (description) {
      updateMetaTag('name', 'description', description);
      updateMetaTag('property', 'og:description', description);
      updateMetaTag('name', 'twitter:description', description);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('name', 'keywords', keywords);
    }

    // Update canonical URL
    if (canonical) {
      updateLink('canonical', canonical);
      updateMetaTag('property', 'og:url', canonical);
      updateMetaTag('name', 'twitter:url', canonical);
    }

    // Update Open Graph image
    if (image) {
      updateMetaTag('property', 'og:image', image);
      updateMetaTag('name', 'twitter:image', image);
    }

    // Update Open Graph type
    if (type) {
      updateMetaTag('property', 'og:type', type);
    }
  }, [title, description, keywords, canonical, image, type]);

  return null;
}

// Helper function to update or create meta tags
function updateMetaTag(attribute, key, content) {
  if (!content) return;
  
  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  
  if (element) {
    element.setAttribute('content', content);
  } else {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    element.setAttribute('content', content);
    document.head.appendChild(element);
  }
}

// Helper function to update or create link tags
function updateLink(rel, href) {
  if (!href) return;
  
  let element = document.querySelector(`link[rel="${rel}"]`);
  
  if (element) {
    element.setAttribute('href', href);
  } else {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    element.setAttribute('href', href);
    document.head.appendChild(element);
  }
}
